import { useCallback, useRef } from 'react';
import { useFetch } from './useFetch';

interface TrackEvent {
  action: string;
  meta_data?: Record<string, any>;
}

export const useAnalytics = () => {
  const { requestApi } = useFetch('/v1/analytics', { actionFeedback: false });
  const errorCountRef = useRef(0);

  const track = useCallback(
    async (event: TrackEvent) => {
      if (errorCountRef.current >= 3) return;

      const response = await requestApi('post', event, '/v1/analytics/track');
      if (response?.ok === false) {
        errorCountRef.current += 1;
        return;
      }
      errorCountRef.current = 0;
    },
    [requestApi]
  );

  const trackPageView = useCallback(
    (page: string) => {
      track({
        action: 'page_view',
        meta_data: { page },
      });
    },
    [track]
  );

  const trackModuleView = useCallback(
    (moduleId: number) => {
      track({
        action: 'module_view',
        meta_data: { module_id: moduleId },
      });
    },
    [track]
  );

  const trackWeekView = useCallback(
    (weekId: number) => {
      track({
        action: 'week_view',
        meta_data: { week_id: weekId },
      });
    },
    [track]
  );

  const trackMeetingView = useCallback(
    (weekId: number, meetingId?: number) => {
      track({
        action: 'meeting_view',
        meta_data: { week_id: weekId, meeting_id: meetingId },
      });
    },
    [track]
  );

  const trackMaterialView = useCallback(
    (materialId: number) => {
      track({
        action: 'material_view',
        meta_data: { material_id: materialId },
      });
    },
    [track]
  );

  const trackMaterialDownload = useCallback(
    (materialId: number) => {
      track({
        action: 'material_download',
        meta_data: { material_id: materialId },
      });
    },
    [track]
  );

  return {
    track,
    trackPageView,
    trackModuleView,
    trackWeekView,
    trackMeetingView,
    trackMaterialView,
    trackMaterialDownload,
  };
};
